<template>
    <div class="search-sheet" :class="{ 'is-open': open }" v-show="open" @keydown.esc="close">
        <!-- A tap on the dimmed page closes the sheet. -->
        <div class="search-sheet__scrim" @click="close"></div>

        <!-- Sits on the keyboard (bottom) and never taller than what it leaves of the
             screen (maxHeight): the field stays put at its foot, the lists above it
             scroll when they do not fit. -->
        <div
            class="search-sheet__panel"
            role="dialog"
            aria-modal="true"
            :aria-label="trans('Search')"
            :style="panelStyle"
        >
            <div class="search-sheet__lists">
                <!-- While typing: suggestions from the catalog, grouped. -->
                <template v-if="suggesting">
                    <section v-for="group in groups" :key="group.kind" class="search-sheet__group">
                        <p class="search-sheet__title">{{ trans(groupTitles[group.kind]) }}</p>
                        <a
                            v-for="item in group.items"
                            :key="item.url"
                            :href="item.url"
                            class="search-sheet__recent search-sheet__suggestion"
                            @click="remember({ label: item.label, url: item.url })"
                        >
                            <svg class="search-sheet__icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                                <path :d="icons.location" fill="currentColor" fill-rule="evenodd" clip-rule="evenodd"/>
                            </svg>
                            <span class="search-sheet__recent-label">
                                <span class="search-sheet__suggestion-label">
                                    <template v-for="(part, index) in highlight(item.label)"><b v-if="part.hit" :key="index">{{ part.text }}</b><template v-else>{{ part.text }}</template></template>
                                </span>
                                <span v-if="item.sub" class="search-sheet__suggestion-sub">{{ item.sub }}</span>
                            </span>
                            <svg class="search-sheet__icon" viewBox="0 0 20 20" aria-hidden="true" focusable="false">
                                <path :d="icons.chevron" fill="currentColor" fill-rule="evenodd" clip-rule="evenodd"/>
                            </svg>
                        </a>
                    </section>
                    <p v-if="!groups.length && !loading" class="search-sheet__recent search-sheet__recent_empty">
                        <svg class="search-sheet__icon" viewBox="0 0 20 20" aria-hidden="true" focusable="false">
                            <path :d="icons.search" fill="currentColor" fill-rule="evenodd" clip-rule="evenodd"/>
                        </svg>
                        <span class="search-sheet__recent-label">{{ trans('No matches — tap Search to look through all listings') }}</span>
                    </p>
                </template>

                <template v-else>
                    <!-- Always there, so the visitor knows a history will build up here. -->
                    <section class="search-sheet__group">
                        <p class="search-sheet__title">{{ trans('Recent searches') }}</p>
                        <p v-if="!recent.length" class="search-sheet__recent search-sheet__recent_empty">
                            <svg class="search-sheet__icon" viewBox="0 0 20 20" aria-hidden="true" focusable="false">
                                <path :d="icons.history" fill="currentColor" fill-rule="evenodd" clip-rule="evenodd"/>
                            </svg>
                            <span class="search-sheet__recent-label">{{ trans('No recent searches yet') }}</span>
                        </p>
                        <a
                            v-for="item in recent"
                            :key="item.url"
                            :href="item.url"
                            class="search-sheet__recent"
                            @click="remember(item)"
                        >
                            <svg class="search-sheet__icon" viewBox="0 0 20 20" aria-hidden="true" focusable="false">
                                <path :d="icons.history" fill="currentColor" fill-rule="evenodd" clip-rule="evenodd"/>
                            </svg>
                            <span class="search-sheet__recent-label">{{ item.label }}</span>
                            <svg class="search-sheet__icon" viewBox="0 0 20 20" aria-hidden="true" focusable="false">
                                <path :d="icons.chevron" fill="currentColor" fill-rule="evenodd" clip-rule="evenodd"/>
                            </svg>
                        </a>
                    </section>

                    <section v-if="popular.length" class="search-sheet__group search-sheet__group_popular">
                        <p class="search-sheet__title">{{ trans('Popular locations') }}</p>
                        <div class="search-sheet__chips">
                            <a
                                v-for="city in popular"
                                :key="city.slug"
                                :href="city.url"
                                class="search-sheet__chip"
                                @click="remember({ label: city.label, url: city.url })"
                            >{{ city.label }}</a>
                        </div>
                    </section>
                </template>
            </div>

            <form class="search-sheet__row" role="search" @submit.prevent="submit">
                <label class="search-sheet__field">
                    <svg class="search-sheet__icon" viewBox="0 0 20 20" aria-hidden="true" focusable="false">
                        <path :d="icons.search" fill="currentColor" fill-rule="evenodd" clip-rule="evenodd"/>
                    </svg>
                    <input
                        ref="input"
                        v-model.trim="term"
                        type="search"
                        enterkeyhint="search"
                        class="search-sheet__input"
                        autocomplete="off"
                        autocorrect="off"
                        autocapitalize="off"
                        spellcheck="false"
                        :placeholder="trans('City, address or ZIP code')"
                        :aria-label="trans('City, address or ZIP code')"
                    >
                </label>
                <button type="submit" class="search-sheet__submit">{{ trans('Search') }}</button>
            </form>
        </div>
    </div>
</template>

<script>
/**
 * The phone's search sheet (Figma "02 — Поиск в фокусе").
 *
 * Opened by any `[data-search-open]` — the Search button of the header's floating
 * actions — it dims the page and raises a sheet over the keyboard: the visitor's
 * recent searches, the most-listed cities and the field. A term goes to the catalog
 * as `?q=` (the search the catalog already runs: MLS number, address, subdivision,
 * ZIP); a city chip goes to its city page. While the visitor types, the lists give way
 * to suggestions from the catalog (/idx/suggest, SPEC-0032), grouped by kind.
 *
 * Recent searches live in this browser only (localStorage): nothing about what a
 * visitor looked for is sent anywhere or kept on the server.
 */
const RECENT_KEY = 'bonadomus:recent-searches';
const RECENT_MAX = 7;

/** The Figma icons (history, chevron-right, search, location), shared by the rows. */
const ICONS = {
    history: 'M4.0475 10C4.0475 6.055 7.24583 2.8575 11.1908 2.8575C15.135 2.8575 18.3333 6.055 18.3333 10C18.3333 13.945 15.135 17.1425 11.1908 17.1425C9.21417 17.1425 7.43667 16.3408 6.1425 15.0475L7.27 13.92C8.27 14.9283 9.65917 15.5558 11.1908 15.5558C14.2617 15.5558 16.7467 13.0708 16.7467 10C16.7467 6.92833 14.2617 4.44417 11.1908 4.44417C8.11833 4.44417 5.635 6.92833 5.635 10H8.01583L4.80917 13.1983L4.75417 13.0875L1.66667 10H4.0475ZM10.3967 10.7942V6.825H11.5867V10.1983L14.365 11.8492L13.7942 12.8092L10.3967 10.7942Z',
    chevron: 'M5.4975 1.90167C5.65417 1.745 5.84444 1.66667 6.06833 1.66667C6.29222 1.66667 6.48278 1.745 6.64 1.90167L14.7375 10L6.64 18.0983C6.48278 18.255 6.29222 18.3333 6.06833 18.3333C5.84444 18.3333 5.65417 18.255 5.4975 18.0983C5.34083 17.9411 5.2625 17.7506 5.2625 17.5267C5.2625 17.3028 5.34083 17.1125 5.4975 16.9558L12.4533 10L5.4975 3.04417C5.34083 2.8875 5.2625 2.69722 5.2625 2.47333C5.2625 2.24944 5.34083 2.05889 5.4975 1.90167Z',
    search: 'M13.4017 14.4858C12.8489 14.8681 12.2358 15.1656 11.5625 15.3783C10.8892 15.5911 10.1911 15.6975 9.46833 15.6975C8.49056 15.6975 7.57639 15.5131 6.72583 15.1442C5.87583 14.7903 5.13556 14.2944 4.505 13.6567C3.87389 13.0189 3.37417 12.2747 3.00583 11.4242C2.6375 10.5742 2.45333 9.66 2.45333 8.68167C2.45333 7.71833 2.6375 6.81139 3.00583 5.96083C3.37417 5.11028 3.87389 4.36611 4.505 3.72833C5.13556 3.09111 5.87583 2.58806 6.72583 2.21917C7.57639 1.85083 8.49056 1.66667 9.46833 1.66667C10.4322 1.66667 11.3394 1.85083 12.19 2.21917C13.0544 2.58806 13.8019 3.09111 14.4325 3.72833C15.0631 4.36611 15.5625 5.11028 15.9308 5.96083C16.2997 6.81139 16.4842 7.71833 16.4842 8.68167C16.4842 9.64556 16.3033 10.5528 15.9417 11.4033C15.5806 12.2533 15.0881 12.9903 14.4642 13.6142L17.5467 17.4833L16.4842 18.3333L13.4017 14.4858ZM15.1233 8.68167C15.1233 7.90222 14.9744 7.1725 14.6767 6.4925C14.3794 5.79806 13.9756 5.19556 13.465 4.685C12.955 4.175 12.3528 3.77111 11.6583 3.47333C10.9778 3.17611 10.2478 3.0275 9.46833 3.0275C8.68889 3.0275 7.95194 3.17611 7.2575 3.47333C6.5775 3.77111 5.98222 4.175 5.47167 4.685C4.96167 5.19556 4.55778 5.79806 4.26 6.4925C3.96278 7.1725 3.81417 7.90222 3.81417 8.68167C3.81417 9.46111 3.96278 10.1981 4.26 10.8925C4.55778 11.5731 4.96167 12.1719 5.47167 12.6892C5.98222 13.2064 6.5775 13.6067 7.2575 13.89C7.95194 14.1878 8.68889 14.3367 9.46833 14.3367C10.2478 14.3367 10.9778 14.1878 11.6583 13.89C12.3528 13.6067 12.955 13.2064 13.465 12.6892C13.9756 12.1719 14.3794 11.5731 14.6767 10.8925C14.9744 10.1981 15.1233 9.46111 15.1233 8.68167Z',
    location: 'M4.002 9.995V9.993C4.002 7.79033 4.78367 5.90733 6.347 4.344C7.911 2.78133 9.79533 2 12 2C14.2047 2 16.089 2.78167 17.653 4.345C19.2163 5.909 19.998 7.79333 19.998 9.998V10C20.01 12.6813 18.6807 15.485 16.01 18.411C15.1753 19.325 14.2567 20.1967 13.254 21.026C12.9387 21.286 12.6357 21.5253 12.345 21.744L12.001 22L11.703 21.781C10.3137 20.761 9.038 19.6103 7.876 18.329C5.282 15.467 3.99067 12.689 4.002 9.995ZM5.002 9.997C4.99133 12.433 6.19633 14.9867 8.617 17.658C9.649 18.7973 10.7753 19.8287 11.996 20.752C12.1967 20.5967 12.4037 20.431 12.617 20.255C13.5837 19.4557 14.4683 18.6163 15.271 17.737C17.7663 15.003 19.0087 12.424 18.998 10V9.998C18.998 8.06933 18.314 6.42067 16.946 5.052C15.5773 3.684 13.9287 3 12 3C10.0713 3 8.42267 3.684 7.054 5.052C5.686 6.41933 5.002 8.06633 5.002 9.993V9.995V9.997ZM7.001 9.998C7.001 8.61733 7.489 7.439 8.465 6.463C9.441 5.487 10.6193 4.999 12 4.999C13.3807 4.999 14.559 5.487 15.535 6.463C16.511 7.439 16.999 8.61733 16.999 9.998C16.999 11.3787 16.511 12.557 15.535 13.533C14.559 14.509 13.3807 14.997 12 14.997C10.6193 14.997 9.441 14.509 8.465 13.533C7.489 12.557 7.001 11.3787 7.001 9.998ZM8.001 9.998C8.001 11.1027 8.39133 12.0453 9.172 12.826C9.95267 13.6067 10.8953 13.997 12 13.997C13.1053 13.997 14.048 13.6067 14.828 12.826C15.6087 12.0453 15.999 11.1027 15.999 9.998C15.999 8.89333 15.6087 7.95067 14.828 7.17C14.048 6.38933 13.1053 5.999 12 5.999C10.8953 5.999 9.95267 6.38933 9.172 7.17C8.39133 7.95 8.001 8.89267 8.001 9.998Z',
};

/** Group headings of the suggestions, by kind (SearchSuggestions on the server). */
const GROUP_TITLES = {
    city: 'Cities',
    district: 'Neighborhoods',
    zip: 'ZIP codes',
    subdivision: 'Communities',
    address: 'Addresses',
};

/** How long typing has to pause before the suggestions are asked for. */
const SUGGEST_DELAY_MS = 200;

/** Room left above the panel, so the page still shows it is a sheet over the page. */
const TOP_GAP = 64;


function readRecent() {
    try {
        const list = JSON.parse(window.localStorage.getItem(RECENT_KEY) || '[]');

        return Array.isArray(list)
            ? list.filter((item) => item && typeof item.label === 'string' && typeof item.url === 'string')
            : [];
    } catch (error) {
        return [];
    }
}

export default {
    name: 'SearchSheet',

    props: {
        // { url: catalog address, popular: [{ slug, label, url }] } — see the
        // search-sheet partial and its view composer.
        options: {
            type: Object,
            required: true,
        },
    },

    data() {
        return {
            open: false,
            term: '',
            recent: [],
            // How far the on-screen keyboard reaches up the window, so the sheet
            // sits on top of it rather than behind it, and how much screen it leaves.
            keyboard: 0,
            room: null,
            // Suggestions for the term typed, grouped by kind.
            groups: [],
            loading: false,
            icons: ICONS,
            groupTitles: GROUP_TITLES,
        };
    },

    computed: {
        popular() {
            return this.options.popular || [];
        },
        /** The catalog's own minimum (SearchSuggestions::MIN_TERM): a shorter term is no filter. */
        minTerm() {
            return this.options.min_term || 2;
        },
        /** Suggestions replace the lists once there is something to suggest for. */
        suggesting() {
            return this.term.length >= this.minTerm && Boolean(this.options.suggest_url);
        },
        panelStyle() {
            return {
                bottom: this.keyboard + 'px',
                maxHeight: this.room === null ? null : Math.max(this.room - TOP_GAP, 0) + 'px',
            };
        },
    },

    watch: {
        term() {
            clearTimeout(this.suggestTimer);

            if (!this.suggesting) {
                this.groups = [];

                return;
            }

            this.loading = true;
            this.suggestTimer = setTimeout(() => this.suggest(), SUGGEST_DELAY_MS);
        },
    },

    mounted() {
        document.addEventListener('click', this.onOpenClick);
    },

    beforeDestroy() {
        clearTimeout(this.suggestTimer);
        if (this.suggestAbort) this.suggestAbort.abort();
        document.removeEventListener('click', this.onOpenClick);
        this.unwatchKeyboard();
        document.documentElement.classList.remove('search-sheet-open');
    },

    methods: {
        onOpenClick(event) {
            if (event.target.closest && event.target.closest('[data-search-open]')) {
                event.preventDefault();
                this.show();
            }
        },

        /**
         * Synchronous on purpose, from the tap itself: iOS opens the keyboard only
         * for a focus() made inside the user's gesture, and the sheet is always in
         * the DOM (v-show) so the field can take it at once.
         */
        show() {
            this.recent = readRecent();
            this.open = true;
            document.documentElement.classList.add('search-sheet-open');
            // Vue would unhide it on its next tick, after the gesture: unhidden now
            // so the field can be focused within the tap.
            this.$el.style.display = '';
            this.$refs.input.focus();
            this.watchKeyboard();
        },

        close() {
            this.open = false;
            this.keyboard = 0;
            document.documentElement.classList.remove('search-sheet-open');
            this.unwatchKeyboard();
            this.$refs.input.blur();
        },

        submit() {
            if (this.term.length < this.minTerm) {
                this.$refs.input.focus();

                return;
            }

            const url = this.options.url + '?' + new URLSearchParams({ q: this.term }).toString();
            this.remember({ label: this.term, url });
            window.location.href = url;
        },

        /** Asks the catalog for suggestions; an answer to an older term is dropped. */
        async suggest() {
            if (this.suggestAbort) this.suggestAbort.abort();
            const controller = new AbortController();
            this.suggestAbort = controller;
            const term = this.term;

            try {
                const params = new URLSearchParams({ q: term, locale: this.options.locale || '' });
                const response = await fetch(this.options.suggest_url + '?' + params.toString(), {
                    signal: controller.signal,
                    headers: { Accept: 'application/json' },
                    credentials: 'same-origin',
                });
                const data = response.ok ? await response.json() : { groups: [] };

                if (term === this.term) {
                    this.groups = Array.isArray(data.groups) ? data.groups : [];
                }
            } catch (error) {
                if (error.name !== 'AbortError' && term === this.term) {
                    this.groups = [];
                }
            } finally {
                if (this.suggestAbort === controller) this.loading = false;
            }
        },

        /** The label in parts, the ones that start with what was typed marked. */
        highlight(label) {
            const term = this.term.toLowerCase();
            const lower = label.toLowerCase();
            const at = lower.startsWith(term) ? 0 : lower.indexOf(' ' + term) + 1;

            if (!term || at <= 0 && !lower.startsWith(term)) {
                return [{ text: label, hit: false }];
            }

            return [
                { text: label.slice(0, at), hit: false },
                { text: label.slice(at, at + term.length), hit: true },
                { text: label.slice(at + term.length), hit: false },
            ].filter((part) => part.text !== '');
        },

        /** Newest first, one entry per address, a few at most. */
        remember(item) {
            const list = [item, ...readRecent().filter((entry) => entry.url !== item.url)].slice(0, RECENT_MAX);

            try {
                window.localStorage.setItem(RECENT_KEY, JSON.stringify(list));
            } catch (error) {
                // Private mode or a full store: the search still goes ahead.
            }
        },

        /**
         * A fixed sheet is placed against the layout viewport, which the keyboard
         * does not shrink on iOS; the visual viewport says how much of the window is
         * left above it.
         */
        watchKeyboard() {
            const viewport = window.visualViewport;

            if (!viewport) {
                return;
            }

            this.onViewport = () => {
                this.keyboard = Math.max(0, Math.round(window.innerHeight - viewport.height - viewport.offsetTop));
                this.room = Math.round(viewport.height);
            };
            viewport.addEventListener('resize', this.onViewport);
            viewport.addEventListener('scroll', this.onViewport);
            this.onViewport();
        },

        unwatchKeyboard() {
            const viewport = window.visualViewport;

            if (viewport && this.onViewport) {
                viewport.removeEventListener('resize', this.onViewport);
                viewport.removeEventListener('scroll', this.onViewport);
            }
            this.onViewport = null;
        },
    },
};
</script>
